import React, { useState, useMemo, useCallback, useEffect, useImperativeHandle } from 'react';
import { InteractiveTrackMap } from '@/components/common/trackmap/InteractiveTrackMap';
import type { OverlayUtils } from '@/components/common/trackmap/InteractiveTrackMap';
import type { TelemetrySample } from '@/types/telemetry';
import type { TrackShape, TrackAnnotation } from '@/lib/spline';
import { projectToLocalMeters, findClosestPointOnPolyline } from '@/lib/map/mapGeometry';
import { valueToColor, divergentValueToColor, type TrackColorMode } from '@/lib/track';
import { TrackGradientLegend } from '@/components/common/trackmap/TrackGradientLegend';
import type { TrackMapApi } from '@/lib/charts';

export interface LapTrackMapProps {
  samples: TelemetrySample[];
  refSamples?: TelemetrySample[] | null;
  trackShape?: TrackShape | null;
  trackAnnotations?: TrackAnnotation[];
  upshiftIndices?: number[];
  brakingPointIndices?: number[];
  accelerationPointIndices?: number[];
  activeMode?: TrackColorMode;
  onPointClick?: (sampleIndex: number) => void;
  className?: string;
  apiRef?: React.RefObject<TrackMapApi | null>;
}

export const LapTrackMap: React.FC<LapTrackMapProps> = ({
  samples,
  refSamples,
  trackShape,
  trackAnnotations,
  upshiftIndices = [],
  brakingPointIndices = [],
  accelerationPointIndices = [],
  activeMode: propActiveMode = 'speed',
  onPointClick,
  className = '',
  apiRef,
}) => {
  const [activeMode, setActiveMode] = useState<TrackColorMode>(propActiveMode);
  const [pinnedGpsIdx, setPinnedGpsIdx] = useState<number>(-1);
  const [highlightGpsIdx, setHighlightGpsIdx] = useState<number>(-1);
  const [rangeMin, setRangeMin] = useState<number>(-1);
  const [rangeMax, setRangeMax] = useState<number>(-1);

  // Sync propActiveMode
  useEffect(() => {
    setActiveMode(propActiveMode);
  }, [propActiveMode]);

  // Valid GPS points and mapping
  const { validPoints, sampleToGps, gpsToSample } = useMemo(() => {
    const pts: TelemetrySample[] = [];
    const sToG = new Int32Array(samples.length).fill(-1);
    const gToS: number[] = [];

    let gi = 0;
    for (let i = 0; i < samples.length; i++) {
      if (samples[i].latitudeRaw && samples[i].longitudeRaw) {
        pts.push(samples[i]);
        sToG[i] = gi;
        gToS.push(i);
        gi++;
      }
    }
    return { validPoints: pts, sampleToGps: sToG, gpsToSample: gToS };
  }, [samples]);

  const toGpsIdx = useCallback(
    (sampleIdx: number): number => {
      if (sampleIdx < 0 || sampleIdx >= samples.length) return -1;
      if (sampleToGps[sampleIdx] >= 0) return sampleToGps[sampleIdx];
      for (let d = 1; d <= 5; d++) {
        if (sampleIdx - d >= 0 && sampleToGps[sampleIdx - d] >= 0) return sampleToGps[sampleIdx - d];
        if (sampleIdx + d < samples.length && sampleToGps[sampleIdx + d] >= 0) return sampleToGps[sampleIdx + d];
      }
      return -1;
    },
    [sampleToGps, samples.length]
  );

  // Expose TrackMapApi via imperative handle / ref
  useImperativeHandle(
    apiRef,
    () => ({
      setHighlight(sampleIndex: number) {
        setHighlightGpsIdx(toGpsIdx(sampleIndex));
      },
      setRange(minSample: number, maxSample: number) {
        if (minSample < 0 || maxSample < 0) {
          setRangeMin(-1);
          setRangeMax(-1);
        } else {
          setRangeMin(toGpsIdx(minSample));
          setRangeMax(toGpsIdx(maxSample));
        }
      },
      setPinnedPoint(sampleIndex: number) {
        setPinnedGpsIdx(toGpsIdx(sampleIndex));
      },
      setColorMode(mode: TrackColorMode) {
        setActiveMode(mode);
      },
    }),
    [toGpsIdx]
  );

  // Pre-calculate metric series for color mapping
  const modeData = useMemo(() => {
    const speedValues = validPoints.map(s => s.speedGpsRaw / 10);
    const rpmValues = validPoints.map(s => s.rpm);
    const gforceValues = validPoints.map(s => s.gforceMagnitude ?? 0);
    const accelValues = validPoints.map(s => s.acceleration ?? 0);

    const accelAbsMax = Math.max(Math.abs(Math.min(...accelValues, 0)), Math.abs(Math.max(...accelValues, 0))) || 1;

    return {
      speed: { values: speedValues, min: Math.min(...speedValues, 0), max: Math.max(...speedValues, 100), unit: 'km/h' },
      acceleration: { values: accelValues, min: -accelAbsMax, max: accelAbsMax, unit: 'm/s²', divergent: true },
      rpm: { values: rpmValues, min: Math.min(...rpmValues, 0), max: Math.max(...rpmValues, 15000), unit: 'RPM', inverted: true },
      gforce: { values: gforceValues, min: Math.min(...gforceValues, 0), max: Math.max(...gforceValues, 3), unit: 'G', inverted: true },
    };
  }, [validPoints]);

  const getColor = useCallback(
    (gpsIdx: number) => {
      const md = modeData[activeMode];
      const val = md.values[gpsIdx] ?? 0;
      if (md.divergent) {
        const absMax = Math.max(Math.abs(md.min), Math.abs(md.max)) || 1;
        return divergentValueToColor(val, absMax);
      }
      return md.inverted
        ? valueToColor(val, md.max, md.min)
        : valueToColor(val, md.min, md.max);
    },
    [modeData, activeMode]
  );

  // Reference GPS points for base map
  const referencePoints = useMemo(() => {
    if (refSamples && refSamples.length > 0) {
      return refSamples.map(s => ({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }));
    }
    return validPoints.map(s => ({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }));
  }, [refSamples, validPoints]);

  const [isNearLine, setIsNearLine] = useState(false);

  const anchorCoord = useMemo<GeoCoord>(() => {
    if (validPoints.length === 0) return { lat: 0, lng: 0 };
    return { lat: validPoints[0].latitudeRaw / 1e6, lng: validPoints[0].longitudeRaw / 1e6 };
  }, [validPoints]);

  const metricSamples = useMemo<Point2D[]>(() => {
    if (validPoints.length === 0) return [];
    return validPoints.map(s => projectToLocalMeters({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }, anchorCoord));
  }, [validPoints, anchorCoord]);

  // Click handler to pin or unpin sample
  const handleClick = useCallback(
    (_canvasPt: Point2D, _metricPt: Point2D, geoPt: GeoCoord) => {
      if (!onPointClick || validPoints.length === 0 || metricSamples.length === 0) return;
      const clickLocal = projectToLocalMeters(geoPt, anchorCoord);

      const { progressIndex, distSq } = findClosestPointOnPolyline(clickLocal, metricSamples);
      if (distSq <= 1600) { // Within 40 meters of racing line
        const closestGps = Math.round(progressIndex);
        const originalSampleIdx = gpsToSample[closestGps];
        if (originalSampleIdx !== undefined) {
          onPointClick(originalSampleIdx);
        }
      } else {
        // Clicking outside the racing line unpins
        onPointClick(-1);
      }
    },
    [onPointClick, validPoints, metricSamples, anchorCoord, gpsToSample]
  );

  const handleHover = useCallback(
    (_canvasPt: Point2D | null, _metricPt: Point2D | null, geoPt: GeoCoord | null) => {
      if (!geoPt || validPoints.length === 0 || metricSamples.length === 0) {
        setIsNearLine(false);
        return;
      }
      const hoverLocal = projectToLocalMeters(geoPt, anchorCoord);
      const { distSq } = findClosestPointOnPolyline(hoverLocal, metricSamples);
      setIsNearLine(distSq <= 1600);
    },
    [validPoints, metricSamples, anchorCoord]
  );

  // Overlay render loop
  const renderOverlay = useCallback(
    (ctx: CanvasRenderingContext2D, { toCanvas, anchor, width, height }: OverlayUtils) => {
      if (validPoints.length < 2) return;

      const canvasPts = validPoints.map(s =>
        toCanvas(projectToLocalMeters({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }, anchor))
      );

      // 1. Value-colored racing line
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      for (let i = 1; i < canvasPts.length; i++) {
        const p0 = canvasPts[i - 1];
        const p1 = canvasPts[i];
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.strokeStyle = getColor(i);
        ctx.stroke();
      }

      // 2. Start/Finish point marker
      const startPt = canvasPts[0];
      ctx.beginPath();
      ctx.arc(startPt.x, startPt.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(startPt.x, startPt.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      // 3. Driving Event markers (Braking, Accel, Upshift)
      const drawEventDots = (indices: number[], color: string) => {
        for (const idx of indices) {
          const gi = sampleToGps[idx];
          if (gi >= 0 && gi < canvasPts.length) {
            const p = canvasPts[gi];
            ctx.beginPath();
            ctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.beginPath();
            ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
          }
        }
      };

      drawEventDots(brakingPointIndices, '#ef4444');
      drawEventDots(accelerationPointIndices, '#22c55e');
      drawEventDots(upshiftIndices, '#38bdf8');

      // 4. Zoomed Range Highlight (dimming the rest)
      const hasRange = rangeMin >= 0 && rangeMax >= 0 && rangeMax > rangeMin;
      if (hasRange) {
        ctx.save();
        ctx.lineWidth = 5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#ffffff';
        ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
        ctx.shadowBlur = 8;

        ctx.beginPath();
        for (let i = rangeMin; i <= rangeMax && i < canvasPts.length; i++) {
          const p = canvasPts[i];
          if (i === rangeMin) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
        ctx.restore();
      }

      // 5. Pinned Point Target Indicator
      if (pinnedGpsIdx >= 0 && pinnedGpsIdx < canvasPts.length) {
        const pt = canvasPts[pinnedGpsIdx];
        ctx.save();
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 10, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 4;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      }

      // 6. Highlight / Hover Point Indicator
      if (highlightGpsIdx >= 0 && highlightGpsIdx < canvasPts.length && highlightGpsIdx !== pinnedGpsIdx) {
        const pt = canvasPts[highlightGpsIdx];
        ctx.save();
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#7c5cfc';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();
      }
    },
    [
      validPoints,
      getColor,
      brakingPointIndices,
      accelerationPointIndices,
      upshiftIndices,
      sampleToGps,
      rangeMin,
      rangeMax,
      pinnedGpsIdx,
      highlightGpsIdx,
    ]
  );

  const currentModeData = modeData[activeMode];

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <div className={`relative aspect-square w-full mx-auto overflow-hidden rounded-2xl shadow-2xl border border-white/10 ${className}`}>
        <InteractiveTrackMap
          trackShape={trackShape}
          trackAnnotations={trackAnnotations}
          points={referencePoints}
          renderOverlay={renderOverlay}
          onClick={handleClick}
          onHover={handleHover}
          drawBaseRibbon={false}
          cursor={isNearLine ? 'crosshair' : undefined}
          className="w-full h-full"
        />
      </div>

      {/* Floating Gradient Legend */}
      {currentModeData && (
        <TrackGradientLegend
          min={currentModeData.min}
          max={currentModeData.max}
          unit={currentModeData.unit}
          divergent={currentModeData.divergent}
          inverted={currentModeData.inverted}
        />
      )}
    </div>
  );
};
